import { useEffect, useRef } from "react";
import { trackRecommendationsShown } from "../analytics";

/**
 * Report `RECOMMENDATIONS_SHOWN` — ONCE per engine response, and only while suggestions are
 * genuinely on screen.
 *
 * Split out of `useRecommendations` because "on screen" is the SURFACE's answer, not the flow's.
 * A collapsible widget can hold a full set of suggestions behind a folded tab; reporting those as
 * shown inflates the top of the funnel and makes the click-through rate below it meaningless.
 *
 * ⚠️ The dedup key is the `recommendationId`, not a boolean: re-opening the widget on the SAME
 * response must not report a second impression, but the next response must report a new one.
 */
export function useRecommendationsShown(input: {
  recommendationId?: string;
  /** Are suggestions actually visible right now? */
  isVisible: boolean;
}) {
  const { recommendationId, isVisible } = input;
  const trackedRecommendationIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!isVisible) return;
    if (trackedRecommendationIdRef.current === recommendationId) return;
    trackedRecommendationIdRef.current = recommendationId || null;
    trackRecommendationsShown(recommendationId);
  }, [isVisible, recommendationId]);
}
